import { useCallback, useState } from 'react'
import Confirm from '../components/Confirm'
import useTranslation from './useTranslation'
import type { Order } from '../types'

/**
 * useReturnToClient — flux UNIQUE de « Envoyer au client » (new_mgr) / « Renvoyer au client » (wait_n*), partagé par tous les points d'appel
 * (menus OrderDetail / Orders / CustomerDetail). Affiche une popup de confirmation, puis dispatch
 * vers la bonne transition serveur selon le statut :
 *   - new_mgr        → sendRetailToClient   (brouillon manager → 'new', perms client+admins)
 *   - wait_n2/wait_n1 → returnRetailToClient (en cours de validation → 'new')
 * Dans les deux cas, l'acteur courant (non-admin) perd ses droits AW ; le nettoyage de la copie
 * locale est géré par les fonctions sous-jacentes (awReconcileOrder).
 *
 * Dépendances injectées (pas d'appel interne à useSales, dont l'effet de dérivation du tarif).
 */
export interface ReturnToClientDeps {
   sendRetailToClient: (order: Order) => Promise<boolean>
   returnRetailToClient: (order: Order) => Promise<string | null>
   newMgrStatus: string
}

export default function useReturnToClient(deps: ReturnToClientDeps) {
   const { sendRetailToClient, returnRetailToClient, newMgrStatus } = deps
   const { trans } = useTranslation()

   const [order, setOrder] = useState<Order | null>(null)
   const [onDone, setOnDone] = useState<(() => void) | null>(null)
   const [show, setShow] = useState(false)

   const requestReturnToClient = useCallback((ord: Order | null | undefined, done?: () => void) => {
      if (!ord?.id) return
      setOrder(ord)
      setOnDone(() => done ?? null)
      setShow(true)
   }, [])

   const run = useCallback(async () => {
      if (!order) return
      const done = onDone
      const o = order
      setShow(false)
      setOrder(null)
      setOnDone(null)
      if (o.status === newMgrStatus) await sendRetailToClient(o)
      else await returnRetailToClient(o)
      done?.()
   }, [order, onDone, newMgrStatus, sendRetailToClient, returnRetailToClient])

   const cancel = useCallback(() => {
      setShow(false)
      setOrder(null)
      setOnDone(null)
   }, [])

   const returnToClientPopups = (
      <>
         {show && (
            <Confirm
               yesno
               title={trans(order?.status === newMgrStatus ? '?Envoyer au client' : '?Renvoyer au client')}
               text={trans(order?.status === newMgrStatus ? '?msg_send_to_client' : '?msg_return_to_client')}
               onOK={run}
               onCancel={cancel}
            />
         )}
      </>
   )

   return { requestReturnToClient, returnToClientPopups }
}
